// Ported from case-desk src/theme/tokens.ts (commit 1d18e32, tag architecture-before-das-platform-2026-07-31)
// per docs/architecture/das-web.md § 14 on 2026-08-18: the single source of colors, typography, radii,
// density, layout and breakpoints (das-web § 8.3). Renamed to DAS Web; the branding default points at
// the DAS mark; the system font stack stays download-free.

export const designTokens = {
  branding: {
    defaultLogoUrl: '/branding/das-mark.svg',
    productName: 'DAS Web',
  },
  breakpoints: {
    // Desktop shows the expandable text navigation, tablet starts collapsed, below the supported
    // width DAS Web shows the unsupported-screen state (das-web § 5.7).
    desktop: 1200,
    tablet: 768,
  },
  // Fixed chart colours of every data chart (validated for colour-vision deficiencies against the
  // paper surface): women and men always the same pair, gap values in neutral ink, contributions
  // muted, the 5 % mark a neutral dashed line, and one attention colour for values at the mark.
  // Every mark keeps 3:1 against paper and background (theme.test.ts); `muted` #8a8a93 has 3.42:1
  // against #ffffff and 3.30:1 against #fafbfc (the former #a1a1aa had only 2.56:1).
  chart: {
    attention: '#b45309',
    men: '#005a8d',
    muted: '#8a8a93',
    threshold: '#535963',
    value: '#3f3f46',
    women: '#8b3fd1',
  },
  color: {
    background: '#fafbfc',
    brandSoft: '#eaf3f8',
    error: '#ef4444',
    info: '#2ab0fc',
    link: '#106ba3',
    neutralBorder: '#e5e5e5',
    neutralHover: '#f5f5f5',
    paper: '#ffffff',
    primary: '#005a8d',
    primaryHover: '#004a76',
    success: '#16a34a',
    text: '#18181b',
    textSecondary: '#535963',
    warning: '#f59e0b',
  },
  density: {
    controlHeight: 32,
    gridCellPaddingBlock: 6,
    gridCellPaddingInline: 10,
    navigationItemHeight: 36,
  },
  // Opt-in analytical pages: quiet surfaces and generous plot space, without changing worklists.
  dashboard: {
    contentMaxWidth: 1440,
    paddingInline: 32,
    surfaceRadius: 12,
    surfacePadding: 24,
    gap: 20,
    titleSize: '1.625rem',
    figureSize: '2.375rem',
    chartLabelSize: 13,
    chartSecondarySize: 12,
    pairedChartsMinWidth: 1040,
  },
  layout: {
    brandMarkSize: 28,
    contentMaxWidth: 1600,
    contentPaddingBlock: {
      desktop: 16,
      tablet: 12,
      unsupported: 8,
    },
    contentPaddingInline: {
      desktop: 24,
      tablet: 16,
      unsupported: 12,
    },
    // The command bar holds compact inputs and buttons with breathing room inside its single row.
    contentHeaderHeight: 52,
    detailMaxWidth: 1200,
    formMaxWidth: 640,
    navigationCollapsedWidth: 32,
    navigationHandleSize: 28,
    navigationWidth: 216,
    userAvatarSize: 30,
  },
  radius: {
    control: 6,
    large: 8,
    small: 4,
  },
  typography: {
    fontFamily:
      '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
    fontSize: 14,
  },
} as const;

type BrandColorToken =
  'background' | 'brandSoft' | 'link' | 'paper' | 'primary' | 'primaryHover' | 'text' | 'textSecondary';

export type BrandColorOverrides = Partial<Record<BrandColorToken, string>>;
